import { graphHooks } from '@tunasong/graph-lib/react'
import { Add, Folder, Right, WebAsset } from '@tunasong/icons'
import { canWriteEntity, logger } from '@tunasong/models'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isBandSpace, isFolderContainer } from '@tunasong/schemas'
import { allQueries, useAdvancedSearch } from '@tunasong/search'
import { Button, useParentChain } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, KeyboardEvent } from 'react'
import { useId, useRef, useState } from 'react'
import { useHomeSpaces } from '../../views/use-home-spaces.js'
import styles from './move-picker.module.css'
import type { FolderResult, Trail } from './move-picker-state.js'
import {
  currentFolderTag,
  folderCount,
  folderRow,
  isListed,
  isOfferedResult,
  newFolderFailedText,
  noFolderMatch,
  recentShown,
  resultPath,
  resultTrail,
  unfiledName,
} from './move-picker-state.js'
import { useMoveRecents } from './use-move-recents.js'

interface FolderRowProps {
  folder: Persisted<Entity>
  items: Persisted<Entity>[]
  /** A space at the top level shows its kind, Band or Space */
  space?: boolean
  /** Recent and search results show where the folder is (screens A, C) */
  path?: string
  onOpen(folder: Persisted<Entity>): void
}

/**
 * One folder: open it to go inside (#2543). A space from the shared search, or a search result, carries no ACLs, so
 * the row judges the loaded entity. Rows carry `data-row` for the arrow keys.
 */
export const FolderRow: FC<FolderRowProps> = props => {
  const { folder: listed, items, space = false, path, onOpen } = props
  const { userId } = useCurrentUser()
  const { entity: loaded } = graphHooks.useEntity(listed.id)
  const folder = loaded ?? listed
  const { current, reason } = folderRow({ folder, items, userId })
  const name = folder.name || 'Untitled'
  const note = reason ?? path

  return (
    <button
      type="button"
      data-row=""
      className={cn(styles.row, { [styles.rowUnwritable]: Boolean(reason), [styles.rowTall]: Boolean(note) })}
      onClick={() => onOpen(folder)}
    >
      {space ? (
        <span className={cn(styles.spaceMark, { [styles.spaceMarkBand]: isBandSpace(folder) })} aria-hidden="true">
          {name.charAt(0).toUpperCase()}
        </span>
      ) : (
        <Folder className={styles.icon} aria-hidden="true" />
      )}
      <span className={styles.rowText}>
        <span className={styles.rowName}>{name}</span>
        {note ? <span className={styles.rowNote}>{note}</span> : null}
      </span>
      {space ? <span className={styles.rowKind}>{isBandSpace(folder) ? 'Band' : 'Space'}</span> : null}
      {current ? <span className={styles.tag}>{currentFolderTag}</span> : null}
      <Right className={styles.chevron} aria-hidden="true" />
    </button>
  )
}

interface LevelProps {
  items: Persisted<Entity>[]
  /** Go to a trail: inside a folder, from the space down */
  onGo(trail: Trail): void
}

/** A recent folder, with its path; opening it puts you inside it */
const RecentRow: FC<LevelProps & { folder: Persisted<Entity> }> = props => {
  const { folder, items, onGo } = props
  const chain = useParentChain(folder) as Trail
  const trail = chain.at(-1)?.id === folder.id ? chain : [...chain, folder]
  return (
    <FolderRow
      folder={folder}
      items={items}
      path={trail.map(f => f.name || 'Untitled').join(' › ')}
      onOpen={() => onGo(trail)}
    />
  )
}

/** Skeleton rows while a level loads; still under reduced motion (the stylesheet) */
const LoadingRows: FC = () => (
  <div className={styles.skeletonRows} aria-hidden="true">
    {['62%', '48%', '70%', '40%'].map(width => (
      <div key={width} className={styles.skeletonRow}>
        <span className={styles.skeletonIcon} />
        <span className={styles.skeletonText} style={{ inlineSize: width }} />
      </div>
    ))}
  </div>
)

/** The top level (screen A): Recent, the spaces, then Unfiled, the destination here */
export const TopLevel: FC<LevelProps> = props => {
  const { items, onGo } = props
  const { userId } = useCurrentUser()
  const { spaces, loading } = useHomeSpaces()
  const { recentIds } = useMoveRecents()
  const { entities: recentFolders } = graphHooks.useEntitiesById(recentIds)
  const recent = recentShown({ folders: recentFolders, items, userId })

  return (
    <>
      {recent.length > 0 ? (
        <>
          <h3 className={styles.sectionLabel}>Recent</h3>
          {recent.map(folder => (
            <RecentRow key={folder.id} folder={folder} items={items} onGo={onGo} />
          ))}
        </>
      ) : null}
      <h3 className={styles.sectionLabel}>Spaces</h3>
      {loading && spaces.length === 0 ? <LoadingRows /> : null}
      {spaces
        .filter(folder => isListed(folder, items))
        .map(folder => (
          <FolderRow key={folder.id} folder={folder} items={items} space onOpen={opened => onGo([opened])} />
        ))}
      <div className={styles.divider} />
      {/* Not a button: there is nothing to open. `aria-current` names it as where Move here goes (Joni, #2556) */}
      <div className={cn(styles.row, styles.rowTall, styles.unfiled)} aria-current="location">
        <WebAsset className={styles.icon} aria-hidden="true" />
        <span className={styles.rowText}>
          <span className={styles.rowName}>{unfiledName}</span>
          <span className={styles.rowNote}>Not in a space or folder</span>
        </span>
      </div>
    </>
  )
}

interface NewFolderProps {
  parent: Persisted<Entity>
  onCreated(folder: Persisted<Entity>): void
}

/** New folder (screen D): a "Folder name" field; Enter creates the folder and opens it, Esc cancels */
const NewFolder: FC<NewFolderProps & { prominent?: boolean }> = props => {
  const { parent, onCreated, prominent = false } = props
  const { createEntity } = graphHooks.useEntityCreate()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  /** The name a create failed for: the field keeps it, and says so (Joni, #2568) */
  const [failedFor, setFailedFor] = useState<string | null>(null)
  const errorId = useId()
  /**
   * One create at a time. The field is read-only while it runs, which keeps focus but not keys (Joni, #2568), and two
   * Enters can arrive before `creating` re-renders, so the guard is a ref.
   */
  const inFlight = useRef(false)

  const handleKeyDown = async (ev: KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Escape') {
      /** Esc cancels the field, not the picker */
      ev.preventDefault()
      ev.stopPropagation()
      setEditing(false)
      setName('')
      setFailedFor(null)
      return
    }
    if (ev.key !== 'Enter' || ev.ctrlKey || ev.metaKey || !name.trim()) {
      return
    }
    ev.preventDefault()
    if (inFlight.current) {
      return
    }
    inFlight.current = true
    const folderName = name.trim()
    setCreating(true)
    setFailedFor(null)
    try {
      const created = await createEntity({ parent, entity: { type: 'folder', name: folderName } })
      if (created) {
        onCreated(created)
        return
      }
      logger.warn('Move picker: New folder came back empty')
    } catch (error) {
      logger.warn('Move picker: New folder failed', error)
    }
    inFlight.current = false
    setCreating(false)
    setFailedFor(folderName)
  }

  if (editing) {
    return (
      <div className={styles.newFolder}>
        <Folder className={styles.icon} aria-hidden="true" />
        <label className={styles.newFolderField}>
          <span className={styles.newFolderLabel}>Folder name</span>
          <input
            className={styles.newFolderInput}
            autoFocus
            /** Read-only, not disabled, while it creates: a disabled field drops focus (Joni, #2568) */
            readOnly={creating}
            aria-busy={creating || undefined}
            value={name}
            aria-invalid={failedFor ? true : undefined}
            aria-describedby={failedFor ? errorId : undefined}
            onChange={ev => setName(ev.target.value)}
            onKeyDown={ev => void handleKeyDown(ev)}
          />
          {failedFor ? (
            <span id={errorId} role="alert" className={styles.newFolderError}>
              {newFolderFailedText(failedFor)}
            </span>
          ) : null}
        </label>
      </div>
    )
  }
  return prominent ? (
    <Button variant="outlined" startIcon={<Add aria-hidden="true" />} data-row="" onClick={() => setEditing(true)}>
      New folder
    </Button>
  ) : (
    <button type="button" data-row="" className={cn(styles.row, styles.newFolderRow)} onClick={() => setEditing(true)}>
      <Add className={styles.icon} aria-hidden="true" />
      <span className={styles.rowName}>New folder</span>
    </button>
  )
}

/** Inside a folder (screen B): its folders, New folder where I can add, and the loading, error and empty states */
export const FolderLevel: FC<LevelProps & { trail: Trail }> = props => {
  const { trail, items, onGo } = props
  const { userId } = useCurrentUser()
  const listed = trail.at(-1) as Persisted<Entity>
  const { entity: loaded } = graphHooks.useEntity(listed.id)
  const folder = loaded ?? listed
  const {
    entities = [],
    isSuccess,
    isLoading,
    isError,
    refetch,
  } = graphHooks.useEntityChildren({ parentId: folder.id, filter: isFolderContainer })
  const folders = entities.filter(child => isListed(child, items))
  const canAdd = Boolean(loaded) && canWriteEntity(userId, folder)
  const handleCreated = (created: Persisted<Entity>) => onGo([...trail, created])

  if (isError) {
    return (
      <div className={styles.message} role="alert">
        <p>Couldn’t load your folders.</p>
        <Button variant="outlined" data-row="" onClick={() => void refetch?.()}>
          Try again
        </Button>
      </div>
    )
  }
  if (isLoading || !isSuccess) {
    return <LoadingRows />
  }
  if (folders.length === 0) {
    return (
      <div className={styles.message}>
        <p>No folders in {folder.name || 'Untitled'} yet.</p>
        {canAdd ? <NewFolder parent={folder} prominent onCreated={handleCreated} /> : null}
      </div>
    )
  }
  return (
    <>
      {folders.map(child => (
        <FolderRow key={child.id} folder={child} items={items} onOpen={opened => onGo([...trail, opened])} />
      ))}
      {canAdd ? <NewFolder parent={folder} onCreated={handleCreated} /> : null}
    </>
  )
}

const folderTypes = ['folder', 'bandspace'] as const
const searchSize = 50

/** Search (screen C): folders at any depth, flat, each with its path */
export const SearchLevel: FC<LevelProps & { query: string }> = props => {
  const { query, items, onGo } = props
  const filter = { types: [...folderTypes] }
  const { results, searching } = useAdvancedSearch(
    query ? allQueries.default({ query, size: searchSize, filter }) : undefined,
    {
      queryType: 'default',
      debounceDelay: 200,
      size: searchSize,
      intent: { query, size: searchSize, types: filter.types },
    }
  )
  const offered = (results as FolderResult[]).filter(result => isOfferedResult(result, items))

  /**
   * One polite status, always mounted, whose text changes (Joni, #2568): a live region inserted with its content is
   * often skipped. Nothing while the search runs, so no "0 folders" flash.
   */
  const none = !searching && offered.length === 0
  const status = searching ? '' : none ? noFolderMatch(query) : folderCount(offered.length)
  return (
    <>
      <p className={none ? styles.empty : styles.resultCount} role="status" data-move-picker-status="">
        {status}
      </p>
      {offered.map(result => (
        <FolderRow
          key={result.id}
          folder={result}
          items={items}
          /** A space at the top has no path but its own name, which the row already shows */
          path={result.parentChain?.length ? resultPath(result) : undefined}
          onOpen={() => onGo(resultTrail(result))}
        />
      ))}
    </>
  )
}
